import { getColorsForTheme } from '@kinet-open-network/shared/util';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { blue, sky, slate, yellow } from 'tailwindcss/colors';

const invertPallete = (pallete: Record<string, string>) => {
  const inverted: Record<string, string> = {};
  const keys = Object.keys(pallete);
  const values = Object.values(pallete);
  keys.forEach((key, index) => {
    inverted[key] = values[values.length - 1 - index];
  });

  return inverted;
};

export const Theme = definePreset(Aura, {
  primitive: {
    red: {
      50: '#FFF1F1',
      100: '#FFD4D4',
      200: '#FFACAC',
      300: '#FF8080',
      400: '#FF4D4D',
      500: '#FF1A1A',
      600: '#B30000',
      700: '#800000',
      800: '#5E0202',
      900: '#4F0000',
    },
    blue,
    gray: {
      100: '#f5f5f5',
      200: '#e3e3e3',
      300: '#ccc9c9',
      400: '#a7a7a7',
      500: '#878787',
      600: '#727272',
      700: '#5f5e5e',
      800: '#515151',
      900: '#272727',
    },
    yellow,
    orange: {
      50: '#FFF6EA',
      100: '#FFECD1',
      200: '#FFDEAF',
      300: '#FFC673',
      400: '#FFAA2F',
      500: '#F49000',
      600: '#E06900',
      700: '#B75600',
      800: '#8A4000',
      900: '#5D2C01',
    },
    green: {
      50: '#EEFFF5',
      100: '#DDFFEC',
      200: '#96EBBB',
      300: '#65DB99',
      400: '#2CC971',
      500: '#00A448',
      600: '#01893D',
      700: '#006D30',
      800: '#00471F',
      900: '#002B13',
    },
    black: '#1D1D1D',
    white: '#FFFFFF',
    slate,
    sky,
    ...getColorsForTheme(false),
  },
  semantic: {
    primary: {
      50: '#F0F6FF',
      100: '#E5EEFC',
      200: '#CDDDF5',
      300: '#A8BFE7',
      400: '#7094D1',
      500: '#2D5EB1',
      600: '#153B7A',
      700: '#0B2B61',
      800: '#001840',
      900: '#000C20',
    },
    secondary: slate,
    colorScheme: {
      light: {
        primary: {
          color: '#2D5EB1',
          inverseColor: '#ffffff',
          hoverColor: '#153B7A',
          activeColor: '#2D5EB1',
        },
      },
      dark: {
        primary: {
          50: '#333540',
          100: '#282932',
          200: '#000000',
          300: '#153B7A',
          400: '#2D5EB1',
          500: '#A8BFE7',
          600: '#7094D1',
          700: '#CDDDF5',
          800: '#E5EEFC',
          900: '#F0F6FF',
          color: '#A8BFE7',
          inverseColor: '#1D1D1D',
          hoverColor: '#7094D1',
          activeColor: '#FFFFFF',
        },
        secondary: invertPallete(slate),
        surface: {
          950: '#0D0D0D',
          900: '#131313',
          800: '#272727',
          700: '#333333',
          600: '#515151',
          500: '#727272',
          400: '#5F5E5E',
          300: '#A7A7A7',
          200: '#CCC9C9',
          100: '#E3E3E3',
          50: '#F5F5F5',
        },
        gray: {
          100: '#272727',
          200: '#515151',
          300: '#5f5e5e',
          400: '#727272',
          500: '#A7A7A7',
          600: '#A7A7A7',
          700: '#CCC9C9',
          800: '#E3E3E3',
          900: '#F5F5F5',
        },
        orange: {
          50: '#5D2C01',
          100: '#B75600',
          200: '#E06900',
          300: '#F49000',
          400: '#FFAA2F',
          500: '#FFAA2F',
          600: '#FFC673',
          700: '#FFDEAF',
          800: '#FFECD1',
          900: '#FFF6EA',
        },
        green: {
          50: '#00471F',
          100: '#006D30',
          200: '#01893D',
          300: '#00A448',
          400: '#2CC971',
          500: '#65DB99',
          600: '#96EBBB',
          700: '#DDFFEC',
          800: '#F6FFFA',
          900: '#FFFFFF',
        },
        red: {
          50: '#4F0000',
          100: '#5E0202',
          200: '#800000',
          300: '#B30000',
          400: '#FF1A1A',
          500: '#FF4D4D',
          600: '#FF8080',
          700: '#FFACAC',
          800: '#FFF1F1',
          900: '#FFFFFF',
        },
        black: '#FFFFFF',
        white: '#131313',
        ...getColorsForTheme(true),
      },
    },
  },
  css: ({ dt }: { dt: (key: string) => string | number | undefined }) => `
    html {
      font-size: 0.875rem;

      .p-tablist-active-bar {
        height: 3px;
      }

      .p-datatable-thead > tr > th {
        background-color: ${dt('primary.100')};
        border-color: ${dt('primary.200')};
      }

      .p-datatable-tbody > tr > td {
        border-color: ${dt('primary.200')};
      }

      div.icon-only-menu.p-menu {
        min-width: unset;
        a.p-menu-item-link {
          width: 2.3rem;
          height: 2.3rem;
          padding: 8.75px;

          .pi-fw {
            width: inherit;
          }
        }
      }

      .ql-toolbar.ql-snow {
        border: 1px solid ${dt('surface.300')} !important;
      }

      .ql-container.ql-snow {
        border: 1px solid ${dt('surface.300')} !important;
      }

      .p-chip:has(.p-chip-remove-icon) {
        padding-inline-end: 0.5rem;
      }
    }

    .p-inputtext:not(.p-inputotp-input),
    .p-dropdown,
    .p-datepicker {
      width: 100%;
    }

    .p-menu {
      a.p-menu-item-link-active {
        background-color: ${dt('primary.200')};
      }

      a.p-menu-item-link {
        color: ${dt('black')};
        padding: 10.5px 14px;
        border-radius: 8px;
      }

      .p-menu-item-icon {
        color: ${dt('black')};
      }

      .p-menu-submenu-label {
        color: ${dt('black')};
      }

      .p-menu-separator {
        border-color: ${dt('primary.200')};
      }
    }

    .p-panelmenu {
      a.p-panelmenu-header-link {
        color: ${dt('black')};

        .p-panelmenu-header-icon {
          color: ${dt('black')};
        }
      }

      .p-panelmenu-panel {
        background-color: ${dt('primary.100')};
      }
    }

    .p-dropdown-items {
      max-width: 100%;
      overflow-x: hidden;
    }

    .p-datatable .p-datatable-tbody > tr {
      height: 45px;
      > th {
        background-color: ${dt('primary.100')};
      }
    }

    .p-datatable .p-datatable-header {
      padding: 0.75rem 0;
      border: none;
    }

    p-tag.p-tag {
      border: 1px solid;
    }

    .p-menu .p-menu-list .p-disabled {
      opacity: 0.5;
    }

    kon-dynamic-form > form > div:has(p-button) {
      display: flex;
      width: fit-content !important;
      background: white;
    }

    .p-tablist-viewport {
      overflow: visible !important;
    }

    .p-tablist {
      overflow-x: auto !important;
    }
  `,
});
